import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, ChevronRight, ChevronDown, Bookmark, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";

const staticNav = [
  { label: "Accueil", href: "/" },
  { label: "Le Projet", href: "/a-propos" },
  { label: "Contact", href: "/contact" },
];

export default function Layout() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [blogOpen, setBlogOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [rubriques, setRubriques] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const [themesOpen, setThemesOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/rubriques"), api.get("/categories")])
      .then(([r, c]) => { setRubriques(r.data); setCategories(c.data); })
      .catch(console.error);
  }, []);

  useEffect(() => { setBlogOpen(false); setThemesOpen(false); setMediaOpen(false); }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const mediaSlugs = ["lives-dofa", "videos"];
  const pubRubriques = rubriques.filter(r => !mediaSlugs.includes(r.slug));
  const mediaRubriques = rubriques.filter(r => mediaSlugs.includes(r.slug));

  const isBlogActive = location.pathname.startsWith("/blog") || location.pathname.startsWith("/newsletter") || (location.pathname.startsWith("/rubrique") && !mediaSlugs.some(s => location.pathname.includes(s)));
  const isThemeActive = location.pathname.startsWith("/categorie");
  const isMediaActive = location.pathname.startsWith("/mediatheque") || mediaSlugs.some(s => location.pathname.includes(s));

  return (
    <div className="min-h-screen flex flex-col font-body">
      <header className="sticky top-0 z-[100] bg-ofa-blue" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
              <img src="/logo-white.png" alt="OFALycée" className="h-10 md:h-12" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {/* Nos Themes Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setThemesOpen(true)}
                onMouseLeave={() => setThemesOpen(false)}
                data-testid="themes-dropdown"
              >
                <button
                  className={`px-3 py-2 text-sm font-bold transition-colors hover:text-white flex items-center gap-1 ${isThemeActive ? "text-white" : "text-white"}`}
                  data-testid="nav-themes"
                >
                  Nos Thèmes
                  <ChevronDown className={`h-3 w-3 transition-transform ${themesOpen ? "rotate-180" : ""}`} />
                </button>
                {themesOpen && (
                  <div className="absolute top-full left-0 mt-0 pt-1 z-50" data-testid="themes-dropdown-menu">
                    <div className="bg-background border border-border shadow-lg min-w-[280px]">
                      <div className="py-2">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/categorie/${cat.slug}`}
                            className="block px-4 py-2.5 text-sm hover:bg-accent hover:text-primary transition-colors"
                            data-testid={`dropdown-theme-${cat.slug}`}
                          >
                            <span className="font-medium">{cat.name}</span>
                            {cat.description && (
                              <span className="block text-[11px] text-muted-foreground mt-0.5 leading-snug">{cat.description.length > 60 ? cat.description.substring(0, 60) + "..." : cat.description}</span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Blog Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setBlogOpen(true)}
                onMouseLeave={() => setBlogOpen(false)}
                data-testid="blog-dropdown"
              >
                <Link
                  to="/blog"
                  className={`px-3 py-2 text-sm font-bold transition-colors hover:text-white flex items-center gap-1 ${isBlogActive ? "text-white" : "text-white"}`}
                  data-testid="nav-blog"
                >
                  Nos Publications
                  <ChevronDown className={`h-3 w-3 transition-transform ${blogOpen ? "rotate-180" : ""}`} />
                </Link>
                {blogOpen && (
                  <div className="absolute top-full left-0 mt-0 pt-1 z-50" data-testid="blog-dropdown-menu">
                    <div className="bg-background border border-border shadow-lg min-w-[220px]">
                      <div className="py-2">
                        <Link
                          to="/blog"
                          className="block px-4 py-2 text-sm font-medium hover:bg-accent hover:text-primary transition-colors"
                          data-testid="dropdown-all-articles"
                        >
                          Tous les articles
                        </Link>
                        <Separator className="my-1" />
                        <p className="px-4 py-1.5 text-[10px] font-mono tracking-widest uppercase text-muted-foreground">Rubriques</p>
                        {pubRubriques.filter(r => !r.external_link && r.slug !== 'newsletter-archive').map((rub) => (
                            <Link
                              key={rub.id}
                              to={`/rubrique/${rub.slug}`}
                              className="block px-4 py-2 text-sm hover:bg-accent hover:text-primary transition-colors"
                              data-testid={`dropdown-${rub.slug}`}
                            >
                              {rub.name}
                            </Link>
                        ))}
                        {pubRubriques.filter(r => r.external_link).map((rub) => (
                            <a
                              key={rub.id}
                              href={rub.external_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2 text-sm hover:bg-accent hover:text-primary transition-colors"
                              data-testid={`dropdown-${rub.slug}`}
                            >
                              {rub.name}
                              <span className="text-[10px] text-muted-foreground ml-1">↗</span>
                            </a>
                        ))}
                        <Separator className="my-1" />
                        <p className="px-4 py-1.5 text-[10px] font-mono tracking-widest uppercase text-muted-foreground">Newsletters</p>
                        <Link
                          to="/la-toile"
                          className="block px-4 py-2 text-sm font-medium hover:bg-accent hover:text-primary transition-colors"
                          data-testid="dropdown-la-toile"
                        >
                          La Toile d'OFALycée
                        </Link>
                        <Link
                          to="/newsletter"
                          className="block px-4 py-2 text-sm hover:bg-accent hover:text-primary transition-colors"
                          data-testid="dropdown-newsletter-archive"
                        >
                          Archives (La Quinzaine)
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Médiathèque Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setMediaOpen(true)}
                onMouseLeave={() => setMediaOpen(false)}
                data-testid="media-dropdown"
              >
                <Link
                  to="/mediatheque"
                  className={`px-3 py-2 text-sm font-bold transition-colors hover:text-white flex items-center gap-1 ${isMediaActive ? "text-white" : "text-white"}`}
                  data-testid="nav-mediatheque"
                >
                  Le Studio
                  <ChevronDown className={`h-3 w-3 transition-transform ${mediaOpen ? "rotate-180" : ""}`} />
                </Link>
                {mediaOpen && (
                  <div className="absolute top-full left-0 mt-0 pt-1 z-50" data-testid="media-dropdown-menu">
                    <div className="bg-background border border-border shadow-lg min-w-[220px]">
                      <div className="py-2">
                        <Link
                          to="/mediatheque"
                          className="block px-4 py-2 text-sm font-medium hover:bg-accent hover:text-primary transition-colors"
                          data-testid="dropdown-videos"
                        >
                          Vidéos
                        </Link>
                        <Separator className="my-1" />
                        {mediaRubriques.filter(r => r.slug !== "videos").map((rub) => (
                          <Link
                            key={rub.id}
                            to={`/rubrique/${rub.slug}`}
                            className="block px-4 py-2 text-sm hover:bg-accent hover:text-primary transition-colors"
                            data-testid={`dropdown-media-${rub.slug}`}
                          >
                            {rub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Link
                to="/a-propos"
                className={`px-3 py-2 text-sm font-bold transition-colors hover:text-white ${location.pathname === "/a-propos" ? "text-white" : "text-white"}`}
                data-testid="nav-a-propos"
              >
                Le Projet
              </Link>
              <Link
                to="/contact"
                className={`px-3 py-2 text-sm font-bold transition-colors hover:text-white ${location.pathname === "/contact" ? "text-white" : "text-white"}`}
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </nav>

            <div className="flex items-center gap-2">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-40 md:w-64 h-9 border-b border-white/40 bg-transparent px-0 rounded-none focus-visible:ring-0 text-white placeholder:text-white/50"
                    autoFocus
                    data-testid="search-input"
                  />
                  <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="text-white hover:bg-white/10" data-testid="close-search-btn">
                    <X className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <>
                  <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="text-white hover:bg-white/10" data-testid="open-search-btn">
                    <Search className="h-4 w-4" />
                  </Button>
                  <Link to="/signets">
                    <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="bookmarks-nav-btn">
                      <Bookmark className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/admin">
                    <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="admin-nav-btn">
                      <UserCircle className="h-4 w-4" />
                    </Button>
                  </Link>
                </>
              )}

              <Sheet>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <nav className="flex flex-col gap-1 mt-8">
                    <Link to="/" className="flex items-center justify-between px-4 py-3 text-base font-medium hover:bg-accent">
                      La Une <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                    <p className="px-4 pt-3 pb-1 text-[10px] font-mono tracking-widest uppercase text-muted-foreground">Nos Thèmes</p>
                    <div className="pl-2">
                      {categories.map((cat) => (
                        <Link key={cat.id} to={`/categorie/${cat.slug}`}
                          className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                          {cat.name} <ChevronRight className="h-3 w-3" />
                        </Link>
                      ))}
                    </div>
                    <Separator className="my-1" />
                    <Link to="/blog" className="flex items-center justify-between px-4 py-3 text-base font-medium hover:bg-accent">
                      Nos Publications <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                    <div className="pl-4">
                      {pubRubriques.filter(r => !r.external_link && r.slug !== 'newsletter-archive').map((rub) => (
                          <Link key={rub.id} to={`/rubrique/${rub.slug}`}
                            className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                            {rub.name} <ChevronRight className="h-3 w-3" />
                          </Link>
                      ))}
                      {pubRubriques.filter(r => r.external_link).map((rub) => (
                          <a key={rub.id} href={rub.external_link} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                            {rub.name} <span className="text-xs">↗</span>
                          </a>
                      ))}
                      <Separator className="my-1 ml-4" />
                      <Link to="/la-toile"
                        className="flex items-center justify-between px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent">
                        La Toile d'OFALycée <ChevronRight className="h-3 w-3" />
                      </Link>
                      <Link to="/newsletter"
                        className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                        Archives (La Quinzaine) <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <Separator className="my-1" />
                    <Link to="/mediatheque" className="flex items-center justify-between px-4 py-3 text-base font-medium hover:bg-accent">
                      Le Studio <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                    <div className="pl-4">
                      <Link to="/mediatheque"
                        className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                        Vidéos <ChevronRight className="h-3 w-3" />
                      </Link>
                      {mediaRubriques.filter(r => r.slug !== "videos").map((rub) => (
                        <Link key={rub.id} to={`/rubrique/${rub.slug}`}
                          className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent">
                          {rub.name} <ChevronRight className="h-3 w-3" />
                        </Link>
                      ))}
                    </div>
                    <Link to="/a-propos" className="flex items-center justify-between px-4 py-3 text-base font-medium hover:bg-accent">
                      Le Projet <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                    <Link to="/contact" className="flex items-center justify-between px-4 py-3 text-base font-medium hover:bg-accent">
                      Contact <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </nav>
                  <Separator className="my-6" />
                  <Link to="/contact" className="block px-4 py-3 text-sm font-medium text-primary">
                    Contactez-nous
                  </Link>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 page-enter">
        <Outlet />
      </main>

      <footer className="bg-ofa-navy text-white mt-20" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <img src="/logo-white.png" alt="OFALycée" className="h-10 mb-4" />
              <p className="text-white/60 max-w-md mb-4 leading-relaxed">
                La plateforme éditoriale d'OFALycée, dédiée à l'éducation bilingue,
                aux transitions académiques et à l'innovation pédagogique.
              </p>
              <p className="text-white/40 text-sm mb-6">OFALycée — Winchester, USA</p>
              <a href="https://info.ofalycee.org/subscribe" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm bg-ofa-blue/20 hover:bg-ofa-blue/30 text-ofa-blue px-4 py-2 rounded-full transition-colors" data-testid="subscribe-footer-btn">
                S'abonner à La Toile
              </a>
            </div>
            <div>
              <h4 className="font-body text-sm font-semibold tracking-wide uppercase mb-6 text-white/40">Rubriques</h4>
              <ul className="space-y-3">
                {rubriques.filter(r => !r.external_link).map((rub) => (
                  <li key={rub.id}>
                    <Link to={`/rubrique/${rub.slug}`} className="text-white/60 hover:text-white transition-colors text-sm">
                      {rub.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-body text-sm font-semibold tracking-wide uppercase mb-6 text-white/40">Thèmes</h4>
              <ul className="space-y-3">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <Link to={`/categorie/${cat.slug}`} className="text-white/60 hover:text-white transition-colors text-sm">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Separator className="my-4 bg-white/10" />
              <ul className="space-y-3">
                <li><Link to="/a-propos" className="text-white/60 hover:text-white transition-colors text-sm">Le Projet</Link></li>
                <li><Link to="/contact" className="text-white/60 hover:text-white transition-colors text-sm">Contact</Link></li>
                <li><a href="https://www.youtube.com/@ofalycee" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors text-sm">Chaîne YouTube</a></li>
                <li><a href="https://ofalycee.org" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors text-sm">OFALycée.org</a></li>
              </ul>
            </div>
          </div>
          <Separator className="my-12 bg-white/10" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/40">
            <p>&copy; {new Date().getFullYear()} OFALycée. Tous droits réservés.</p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-mentions-legales">Mentions légales</Link>
              <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors" data-testid="footer-confidentialite">Confidentialité</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
